import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SideBar from './SideBar';
import TopNavBar from './TopNavBar';
import Footer from './Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function getToken() {
  const session = JSON.parse(sessionStorage.getItem('adminSession'));
  return session?.token || null;
}

function emptyFormState(fields) {
  const state = {};
  fields.forEach((f) => {
    state[f.name] = f.type === 'course-select' ? '' : '';
  });
  return state;
}

function Row({ item, fields, imageField, statusToggle, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id.toString(),
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? '#f9fafb' : undefined,
  };

  return (
    <tr ref={setNodeRef} style={style}>
      <td className="py-12 px-16" style={{ width: 36 }}>
        <span {...attributes} {...listeners} style={{ cursor: 'grab' }} title="Drag to reorder">
          <i className="ph ph-dots-six-vertical text-gray-400" />
        </span>
      </td>
      {imageField && (
        <td className="py-12 px-16" style={{ width: 72 }}>
          {item[imageField] ? (
            <img
              src={`${apiUrl}/${item[imageField]}`}
              alt=""
              style={{ width: 56, height: 42, borderRadius: 6, objectFit: 'cover' }}
            />
          ) : (
            <div style={{
              width: 56, height: 42, borderRadius: 6, background: '#f3f4f6',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <i className="ph ph-image text-gray-300" />
            </div>
          )}
        </td>
      )}
      {fields
        .filter((f) => f.showInList !== false)
        .map((f) => (
          <td className="py-12 px-16 text-14" key={f.name}>
            {f.type === 'course-select'
              ? (item.course_title || '—')
              : (item[f.name] ? String(item[f.name]).slice(0, 80) : '—')}
          </td>
        ))}
      {statusToggle && (
        <td className="py-12 px-16">
          <span className={`badge border-0 py-4 px-12 rounded-pill text-13 fw-medium ${item.status === 1 ? 'bg-success-100 text-success-600' : 'bg-warning-100 text-warning-600'}`}>
            {item.status === 1 ? 'Published' : 'Draft'}
          </span>
        </td>
      )}
      <td className="py-12 px-16">
        <div className="d-flex align-items-center gap-8">
          <button type="button" className="btn btn-sm btn-outline-main rounded-pill py-4 px-12" onClick={() => onEdit(item)}>
            <i className="ph ph-pencil me-1" /> Edit
          </button>
          <button
            type="button"
            className="btn btn-sm rounded-pill py-4 px-12"
            style={{ backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff' }}
            onClick={() => onDelete(item.id)}
          >
            <i className="ph ph-trash me-1" /> Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

// Generic drag-reorderable list-manager for the simple Homepage CMS
// resources (banners/badges/highlights/counters/featured courses). Driven
// entirely by a config object so each resource only needs a thin wrapper
// page rather than a full bespoke admin screen.
function HomeListManager({
  title,
  urlPath,
  fields,
  imageField = null,
  statusToggle = true,
  emptyMessage = 'Nothing here yet — click "Add" to create the first one.',
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyFormState(fields));
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState('');
  const [saving, setSaving] = useState(false);
  const [courseOptions, setCourseOptions] = useState([]);

  const needsCourseOptions = fields.some((f) => f.type === 'course-select');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) {
        setError('You must be logged in to manage this.');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${apiUrl}/api/admin/${urlPath}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setItems(response.data.items);
      } else {
        setError(response.data.message || 'Failed to fetch.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching data.');
    } finally {
      setLoading(false);
    }
  }, [urlPath]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    if (!needsCourseOptions) return;
    axios.get(`${apiUrl}/api/public/course-pages`)
      .then((res) => {
        if (res.data.success) setCourseOptions(res.data.coursePages);
      })
      .catch(() => {});
  }, [needsCourseOptions]);

  const resetForm = () => {
    setForm(emptyFormState(fields));
    setImageFile(null);
    setImagePreview(null);
    setExistingImage('');
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const startAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const startEdit = (item) => {
    const next = {};
    fields.forEach((f) => {
      next[f.name] = item[f.name] ?? '';
    });
    if (statusToggle) next.status = item.status;
    setForm(next);
    setExistingImage(imageField ? item[imageField] || '' : '');
    setImagePreview(null);
    setImageFile(null);
    setEditingId(item.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFieldChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    const formData = new FormData();
    fields.forEach((f) => formData.append(f.name, form[f.name] ?? ''));
    formData.append('position', editingId ? (form.position ?? 0) : items.length + 1);
    if (statusToggle) formData.append('status', form.status ?? 1);
    if (imageFile) formData.append(imageField, imageFile);

    try {
      const token = getToken();
      const url = editingId ? `${apiUrl}/api/admin/${urlPath}/${editingId}` : `${apiUrl}/api/admin/${urlPath}`;
      const method = editingId ? 'put' : 'post';
      const response = await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        setSuccess(editingId ? 'Updated successfully.' : 'Added successfully.');
        resetForm();
        fetchItems();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Failed to save.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item? This cannot be undone.')) return;
    try {
      const token = getToken();
      const response = await axios.delete(`${apiUrl}/api/admin/${urlPath}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        fetchItems();
      } else {
        setError(response.data.message || 'Failed to delete.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting.');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((current) => {
      const oldIndex = current.findIndex((it) => it.id.toString() === active.id);
      const newIndex = current.findIndex((it) => it.id.toString() === over.id);
      const reordered = arrayMove(current, oldIndex, newIndex).map((it, index) => ({ ...it, position: index + 1 }));
      updatePositions(reordered);
      return reordered;
    });
  };

  const updatePositions = async (reordered) => {
    try {
      const token = getToken();
      await axios.put(
        `${apiUrl}/api/admin/${urlPath}/order`,
        reordered.map((it) => ({ id: it.id, position: it.position })),
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      setError('Failed to update order.');
      fetchItems();
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link>
                </li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">{title}</span></li>
              </ul>
            </div>
            <button type="button" className="btn btn-main rounded-pill py-9" onClick={() => (showForm ? resetForm() : startAdd())}>
              <i className={`ph ${showForm ? 'ph-x' : 'ph-plus'} me-2`} /> {showForm ? 'Cancel' : 'Add'}
            </button>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          {showForm && (
            <div className="card mb-24">
              <div className="card-header border-bottom border-gray-100">
                <h5 className="mb-0">{editingId ? 'Edit' : 'Add New'}</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-20">
                    {imageField && (
                      <div className="col-12">
                        <label className="h5 mb-8 fw-semibold font-heading d-block">Image</label>
                        <div className="d-flex align-items-center gap-16">
                          {(imagePreview || existingImage) ? (
                            <img
                              src={imagePreview || `${apiUrl}/${existingImage}`}
                              alt="preview"
                              style={{ width: 100, height: 80, borderRadius: 12, objectFit: 'cover', border: '3px solid #e8f5ee' }}
                            />
                          ) : (
                            <div style={{
                              width: 100, height: 80, borderRadius: 12, background: '#f3f4f6',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed #d1d5db'
                            }}>
                              <i className="ph ph-image text-gray-400" style={{ fontSize: 28 }} />
                            </div>
                          )}
                          <input
                            type="file"
                            className="form-control py-11"
                            accept="image/*"
                            onChange={handleImageChange}
                            disabled={saving}
                            style={{ maxWidth: 320 }}
                          />
                        </div>
                      </div>
                    )}

                    {fields.map((f) => (
                      <div className={f.colClass || 'col-md-6'} key={f.name}>
                        <label htmlFor={f.name} className="h5 mb-8 fw-semibold font-heading">
                          {f.label} {f.required && <span className="text-red-500">*</span>}
                        </label>
                        {f.type === 'textarea' ? (
                          <textarea
                            id={f.name}
                            className="form-control py-11"
                            rows={f.rows || 3}
                            value={form[f.name] || ''}
                            onChange={(e) => handleFieldChange(f.name, e.target.value)}
                            disabled={saving}
                            required={f.required}
                          />
                        ) : f.type === 'course-select' ? (
                          <select
                            id={f.name}
                            className="form-select py-11"
                            value={form[f.name] || ''}
                            onChange={(e) => handleFieldChange(f.name, e.target.value)}
                            disabled={saving}
                            required={f.required}
                          >
                            <option value="">Select a course...</option>
                            {courseOptions.map((c) => (
                              <option key={c.id} value={c.id}>{c.title}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            id={f.name}
                            className="form-control py-11"
                            value={form[f.name] || ''}
                            onChange={(e) => handleFieldChange(f.name, e.target.value)}
                            disabled={saving}
                            required={f.required}
                            placeholder={f.placeholder}
                          />
                        )}
                      </div>
                    ))}

                    {statusToggle && (
                      <div className="col-md-4">
                        <label htmlFor="status" className="h5 mb-8 fw-semibold font-heading">Status</label>
                        <select
                          id="status"
                          className="form-select py-11"
                          value={form.status ?? 1}
                          onChange={(e) => handleFieldChange('status', Number(e.target.value))}
                          disabled={saving}
                        >
                          <option value={1}>Published</option>
                          <option value={0}>Draft</option>
                        </select>
                      </div>
                    )}

                    <div className="col-12 flex-align justify-content-end gap-8">
                      <button type="button" className="btn btn-outline-main rounded-pill py-9" onClick={resetForm}>Cancel</button>
                      <button type="submit" className="btn btn-main rounded-pill py-9" disabled={saving}>
                        {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header border-bottom border-gray-100 flex-between">
              <h5 className="mb-0">All ({items.length})</h5>
              <span className="text-13 text-gray-400"><i className="ph ph-arrows-out-cardinal me-1" /> Drag rows to reorder</span>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : items.length === 0 ? (
                <div className="text-center py-40 text-gray-400">{emptyMessage}</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-12 px-16" />
                        {imageField && <th className="py-12 px-16" />}
                        {fields.filter((f) => f.showInList !== false).map((f) => (
                          <th className="py-12 px-16 text-13 fw-semibold text-gray-500" key={f.name}>{f.label}</th>
                        ))}
                        {statusToggle && <th className="py-12 px-16 text-13 fw-semibold text-gray-500">Status</th>}
                        <th className="py-12 px-16 text-13 fw-semibold text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={items.map((it) => it.id.toString())} strategy={verticalListSortingStrategy}>
                        <tbody>
                          {items.map((item) => (
                            <Row
                              key={item.id}
                              item={item}
                              fields={fields}
                              imageField={imageField}
                              statusToggle={statusToggle}
                              onEdit={startEdit}
                              onDelete={handleDelete}
                            />
                          ))}
                        </tbody>
                      </SortableContext>
                    </DndContext>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default HomeListManager;
